import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { darken, rgbToHex, useTheme } from "@mui/material/styles";
import { useAuth0 } from "@auth0/auth0-react";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Link as RouterLink } from "react-router-dom";
import { Section } from "../settings/Section";
import CompanyLogoUpload from "../../../../components/CompanyLogoUpload/CompanyLogoUpload";
import HeroImageField from "./HeroImageField";
import BrandColorField from "./BrandColorField";
import FaviconField from "./FaviconField";
import FetchBrandingDialog from "./FetchBrandingDialog";
import { uploadTrustCenterFont } from "../../../../apis/trust-center-apis/trustCenterApis";
import {
  FONT_ACCEPT_ATTRIBUTE,
  FONT_HINT,
  validateFont,
} from "../../data/uploadRules";
import { siteName } from "../../data/websiteBranding";
import { EMAIL_PATTERN } from "../../data/accessRules";
import {
  BANNER_STYLES,
  BANNER_TEXT_COLORS,
  BRAND_PRESETS,
  CUSTOM_FONT_KEY,
  CUSTOM_FONT_NAME,
  FONTS,
  FONT_STYLES,
  FONT_WEIGHTS,
  MAX_FONT_FILES,
  isGoogleFont,
  HERO_TITLE_MAX,
  colorProblem,
  fontOf,
  fontStack,
  fontsUrl,
  isHexColor,
} from "../../data/brand";
import { DEFAULT_ACCENT, usePublicFont } from "../../styles/publicTheme";

const URL_PATTERN = /^https?:\/\/\S+$/;
export const MAX_CUSTOM_LINKS = 5;

export const toOverviewForm = (data) => ({
  name: data.company.name || "",
  overview: data.company.overview || "",
  website: data.company.website || "",
  securityEmail: data.company.securityEmail || "",
  termsUrl: data.company.termsUrl || "",
  privacyUrl: data.company.privacyUrl || "",
  dataResidency: data.company.dataResidency || "",
  customLinks: (data.company.customLinks || []).map((link) => ({ ...link })),
  heroImagePath: data.company.heroImagePath || "",
  heroTitle: data.company.heroTitle || "",
  brandColor: data.company.brandColor || "",
  secondaryColor: data.company.secondaryColor || "",
  // Enum values come back as stored (lower case).
  bannerStyle: String(
    data.company.bannerStyle ||
      (data.company.heroImagePath ? "image" : "gradient")
  ).toLowerCase(),
  bannerStartColor: data.company.bannerStartColor || "",
  bannerEndColor: data.company.bannerEndColor || "",
  bannerTextColor: String(data.company.bannerTextColor || "auto").toLowerCase(),
  fontFamily:
    String(data.company.fontFamily || "").toLowerCase() === CUSTOM_FONT_KEY
      ? CUSTOM_FONT_KEY
      : fontOf(String(data.company.fontFamily || "").toLowerCase()).key,
  customFontName: data.company.customFontName || "",
  customFontFiles: (data.company.customFontFiles || []).map(
    ({ path, weight, style }) => ({ path, weight, style })
  ),
  // Not saved: files present means the admin's own files, else Google Fonts.
  customFontSource: (data.company.customFontFiles || []).length
    ? "files"
    : "google",
  faviconPath: data.company.faviconPath || "",
});

export const validateOverview = (form) => {
  const errors = {};
  // Company name, overview and website are read-only here (Company
  // Settings), so they are not checked.
  if (form.heroTitle.trim().length > HERO_TITLE_MAX)
    errors.heroTitle = `Keep the title to ${HERO_TITLE_MAX} characters or fewer.`;
  [
    ["brandColor", true],
    ["secondaryColor", true],
    ["bannerStartColor", false],
    ["bannerEndColor", false],
  ].forEach(([key, onWhite]) => {
    const problem = colorProblem(form[key].trim(), { onWhite });
    if (problem) errors[key] = problem;
  });
  if (form.bannerStyle === "image" && !form.heroImagePath)
    errors.heroImagePath = "Upload an image, or choose another banner style.";
  if (form.fontFamily === CUSTOM_FONT_KEY) {
    const name = form.customFontName.trim();
    if (!name) errors.customFontName = "Enter the font's name.";
    else if (!CUSTOM_FONT_NAME.test(name))
      errors.customFontName =
        "Use letters, numbers, spaces, dots, dashes or underscores (up to 60).";
    if (form.customFontSource === "files" && form.customFontFiles.length === 0)
      errors.customFontFiles =
        "Upload at least one font file, or use Google Fonts.";
  }
  if (!EMAIL_PATTERN.test(form.securityEmail.trim()))
    errors.securityEmail = "Enter a valid email.";
  ["termsUrl", "privacyUrl"].forEach((key) => {
    if (form[key] && !URL_PATTERN.test(form[key].trim()))
      errors[key] = "Start the link with https://";
  });
  const linkErrors = {};
  form.customLinks.forEach((link, index) => {
    const linkError = {};
    if (!link.label.trim()) linkError.label = "Add a name for this link.";
    if (!URL_PATTERN.test(link.url.trim()))
      linkError.url = "Start the link with https://";
    if (Object.keys(linkError).length > 0) linkErrors[index] = linkError;
  });
  if (Object.keys(linkErrors).length > 0) errors.customLinks = linkErrors;
  return errors;
};

export const overviewChanges = (form, data) => {
  // eslint-disable-next-line no-unused-vars
  const { customLinks, customFontFiles, customFontSource, ...fields } = form;
  return {
    company: {
      ...data.company,
      ...Object.fromEntries(
        Object.entries(fields).map(([key, value]) => [key, value.trim()])
      ),
      // Google Fonts are loaded by name: no files.
      customFontFiles:
        customFontSource === "files"
          ? customFontFiles.map(({ path, weight, style }) => ({
              path,
              weight,
              style,
            }))
          : [],
      customLinks: customLinks.map((link) => ({
        id: link.id,
        label: link.label.trim(),
        url: link.url.trim(),
      })),
    },
  };
};

// Extra links shown with the company info, e.g. Status page, Bug bounty.
const CustomLinksEditor = ({ links, errors = {}, onChange }) => {
  const theme = useTheme();
  const updateLink = (index, changes) =>
    onChange(links.map((l, i) => (i === index ? { ...l, ...changes } : l)));
  const nextId = Math.max(0, ...links.map((l) => l.id || 0)) + 1;

  return (
    <Stack spacing={1.5}>
      {links.length === 0 && (
        <Typography
          variant="body2"
          sx={{ color: theme.customColors.text.secondary }}
        >
          No custom links yet.
        </Typography>
      )}
      {links.map((link, index) => (
        <Stack
          key={link.id}
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "flex-start" }}
        >
          <TextField
            size="small"
            placeholder="Link name"
            value={link.label}
            onChange={(e) => updateLink(index, { label: e.target.value })}
            error={!!errors[index]?.label}
            helperText={errors[index]?.label}
            sx={{ flex: { sm: "0 0 38%" } }}
            inputProps={{ maxLength: 40, "aria-label": "Link name" }}
          />
          <TextField
            size="small"
            placeholder="https://"
            value={link.url}
            onChange={(e) => updateLink(index, { url: e.target.value })}
            error={!!errors[index]?.url}
            helperText={errors[index]?.url}
            sx={{ flex: 1 }}
            inputProps={{ "aria-label": "URL" }}
          />
          <Box>
            <Tooltip title="Remove link" arrow>
              <IconButton
                aria-label={`Remove ${link.label || "link"}`}
                onClick={() => onChange(links.filter((_, i) => i !== index))}
                sx={{ mt: 0.25 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={links.length >= MAX_CUSTOM_LINKS}
          onClick={() =>
            onChange([...links, { id: nextId, label: "", url: "" }])
          }
          sx={{ textTransform: "none" }}
        >
          Add link
        </Button>
        {links.length >= MAX_CUSTOM_LINKS && (
          <Typography
            variant="caption"
            sx={{ ml: 1, color: theme.customColors.text.secondary }}
          >
            You can add up to {MAX_CUSTOM_LINKS} links.
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

// Same pill look as People "In Scope / Out of Scope".
export const pillGroupSx = {
  "& .MuiToggleButton-root": {
    textTransform: "none",
    px: 1.5,
    py: 0.5,
    fontSize: 13,
    border: "1px solid #E8EAED",
    "&.Mui-selected": {
      backgroundColor: "#3B82F6",
      color: "#FFFFFF",
      "&:hover": { backgroundColor: "#3B82F6" },
    },
  },
};

const FieldLabel = ({ children, id, htmlFor }) => {
  const theme = useTheme();
  return (
    <Typography
      id={id}
      component={htmlFor ? "label" : "span"}
      htmlFor={htmlFor}
      sx={{
        display: "block",
        fontSize: "0.875rem",
        fontWeight: 600,
        color: theme.customColors.text.strong,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
};

// Label above the box. A floating outline label was getting cut by the
// field border in this dialog.
const LabeledField = ({
  label,
  id,
  helperText,
  error,
  inputProps,
  select,
  ...rest
}) => (
  <Box>
    <FieldLabel htmlFor={select ? undefined : id}>{label}</FieldLabel>
    <TextField
      id={select ? undefined : id}
      select={select}
      // Full height, like the fields in the app's other dialogs.
      size="medium"
      fullWidth
      error={!!error}
      helperText={error || helperText}
      inputProps={{
        ...inputProps,
        ...(select ? { "aria-label": label } : { id }),
      }}
      {...rest}
    />
  </Box>
);

const Pills = ({ id, label, options, value, onChange, helperText }) => {
  const theme = useTheme();
  return (
    <Box>
      <FieldLabel id={id}>{label}</FieldLabel>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        onChange={(_, next) => next && onChange(next)}
        aria-labelledby={id}
        sx={pillGroupSx}
      >
        {options.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.75,
            color: theme.customColors.text.secondary,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

// The banner at the top of the public Overview (like Vanta's: a gradient of
// two colours, an image, or none) and its text colour.
const BannerSection = ({ form, set, errors }) => {
  const primary = isHexColor(form.brandColor)
    ? form.brandColor
    : DEFAULT_ACCENT;
  const companyName = form.name.trim() || "Your company";
  return (
    <Section
      title="Banner"
      subtitle="The top of your public Overview. Changes go live when you publish."
    >
      <LabeledField
        id="overview-hero-title"
        label="Banner title"
        placeholder={`${companyName} Trust Center`}
        value={form.heroTitle}
        onChange={(e) => set({ heroTitle: e.target.value })}
        error={errors.heroTitle}
        helperText={`Leave empty to show “${companyName} Trust Center”. ${form.heroTitle.length}/${HERO_TITLE_MAX} characters`}
        inputProps={{ maxLength: HERO_TITLE_MAX }}
      />
      <Pills
        id="banner-style-label"
        label="Banner style"
        options={BANNER_STYLES}
        value={form.bannerStyle}
        onChange={(bannerStyle) => set({ bannerStyle })}
        helperText={
          form.bannerStyle === "minimal"
            ? "No banner: the title and overview sit on the page."
            : undefined
        }
      />
      {form.bannerStyle === "gradient" && (
        // One above the other: side by side, the settings column is too
        // narrow for the labels.
        <Stack spacing={2.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <BrandColorField
              label="Gradient start colour"
              value={form.bannerStartColor}
              onChange={(bannerStartColor) => set({ bannerStartColor })}
              error={errors.bannerStartColor}
              fallback={rgbToHex(darken(primary, 0.55)).toUpperCase()}
              helperText="Leave both empty for shades of your primary colour."
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <BrandColorField
              label="Gradient end colour"
              value={form.bannerEndColor}
              onChange={(bannerEndColor) => set({ bannerEndColor })}
              error={errors.bannerEndColor}
              fallback={rgbToHex(darken(primary, 0.78)).toUpperCase()}
            />
          </Box>
        </Stack>
      )}
      {form.bannerStyle === "image" && (
        <HeroImageField
          heroImagePath={form.heroImagePath}
          onChange={(heroImagePath) => set({ heroImagePath })}
          error={errors.heroImagePath}
        />
      )}
      {form.bannerStyle !== "minimal" && (
        <Pills
          id="banner-text-label"
          label="Banner text"
          options={BANNER_TEXT_COLORS}
          value={form.bannerTextColor}
          onChange={(bannerTextColor) => set({ bannerTextColor })}
          helperText={
            form.bannerTextColor === "auto"
              ? "White or dark, whichever is easier to read on your banner."
              : form.bannerStyle === "image"
                ? "The image gets a matching tint so the text stays readable."
                : undefined
          }
        />
      )}
    </Section>
  );
};

// A guess at a font file's weight and style from its name
// ("Acme-SemiBoldItalic.woff2" -> 600, italic).
export const fontFileGuess = (fileName) => {
  const name = String(fileName || "").toLowerCase();
  const weight = /variable|\bvf\b|-vf\./.test(name)
    ? "100 900"
    : /semi|demi/.test(name)
      ? "600"
      : /bold|black|heavy/.test(name)
        ? "700"
        : /medium/.test(name)
          ? "500"
          : "400";
  return { weight, style: /italic|oblique/.test(name) ? "italic" : "normal" };
};

const weightLabel = (weight) =>
  FONT_WEIGHTS.find((w) => w.value === weight)?.label ||
  `Weights ${weight.replace(" ", "–")}`;

// The admin's own font: a Google Font by name, or uploaded files (one per
// weight). Files upload at once; the list is saved with the Overview.
const CustomFontFields = ({ form, set, errors }) => {
  const theme = useTheme();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");
  // Google Fonts: whether the name was found ("found", "missing", or "").
  const [googleCheck, setGoogleCheck] = useState("");
  const files = form.customFontFiles;
  const google = form.customFontSource === "google";

  const checkGoogle = async () => {
    const name = form.customFontName.trim();
    if (!google || !CUSTOM_FONT_NAME.test(name)) return setGoogleCheck("");
    const found = await isGoogleFont(name);
    setGoogleCheck(found === null ? "" : found ? "found" : "missing");
    return undefined;
  };

  const addFiles = async (chosen) => {
    const list = Array.from(chosen || []);
    if (list.length === 0) return;
    if (files.length + list.length > MAX_FONT_FILES) {
      setUploadError(`Add up to ${MAX_FONT_FILES} font files.`);
      return;
    }
    const problem = list.map(validateFont).find(Boolean);
    if (problem) {
      setUploadError(problem);
      return;
    }
    setBusy(true);
    setUploadError("");
    try {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;
      const added = [];
      for (const file of list) {
        // eslint-disable-next-line no-await-in-loop
        const { fontPath } = await uploadTrustCenterFont(file, {
          accessToken,
          idToken,
        });
        added.push({
          path: fontPath,
          fileName: file.name,
          ...fontFileGuess(file.name),
        });
      }
      const name =
        form.customFontName.trim() ||
        list[0].name
          .replace(/\.[^.]+$/, "")
          .replace(
            /[-_](regular|bold|medium|semibold|italic|variable|vf).*$/i,
            ""
          )
          .replace(/[^A-Za-z0-9 _.-]/g, " ")
          .trim();
      set({ customFontFiles: [...files, ...added], customFontName: name });
    } catch (e) {
      setUploadError(e.message || "Could not upload the font. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const updateFile = (index, changes) =>
    set({
      customFontFiles: files.map((f, i) =>
        i === index ? { ...f, ...changes } : f
      ),
    });

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
      }}
    >
      <Pills
        id="custom-font-source-label"
        label="Where the font comes from"
        options={[
          { value: "google", label: "Google Fonts" },
          { value: "files", label: "Upload font files" },
        ]}
        value={form.customFontSource}
        onChange={(customFontSource) => {
          setGoogleCheck("");
          set({ customFontSource });
        }}
      />
      <LabeledField
        id="overview-custom-font-name"
        label="Font name"
        placeholder={google ? "Poppins" : "Acme Sans"}
        value={form.customFontName}
        onChange={(e) => {
          setGoogleCheck("");
          set({ customFontName: e.target.value });
        }}
        onBlur={checkGoogle}
        error={
          errors.customFontName ||
          (googleCheck === "missing"
            ? "Not found on Google Fonts. Check the spelling, or upload the font files."
            : "")
        }
        helperText={
          google
            ? googleCheck === "found"
              ? "Found on Google Fonts."
              : "The family name exactly as on fonts.google.com."
            : "The name your page uses for these files."
        }
        inputProps={{ maxLength: 60 }}
      />
      {!google && (
        <Box>
          <FieldLabel>Font files</FieldLabel>
          <Stack spacing={1}>
            {files.map((file, index) => (
              <Stack
                key={file.path}
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ sm: "center" }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: theme.customColors.text.strong,
                  }}
                  title={file.fileName || file.path.split("/").pop()}
                >
                  {file.fileName || `Font file ${index + 1}`}
                </Typography>
                <TextField
                  select
                  size="small"
                  value={file.weight}
                  onChange={(e) =>
                    updateFile(index, { weight: e.target.value })
                  }
                  inputProps={{
                    "aria-label": `Weight of font file ${index + 1}`,
                  }}
                  sx={{ minWidth: 200 }}
                >
                  {[
                    ...FONT_WEIGHTS,
                    ...(FONT_WEIGHTS.some((w) => w.value === file.weight)
                      ? []
                      : [
                          {
                            value: file.weight,
                            label: weightLabel(file.weight),
                          },
                        ]),
                  ].map((w) => (
                    <MenuItem key={w.value} value={w.value}>
                      {w.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  value={file.style}
                  onChange={(e) => updateFile(index, { style: e.target.value })}
                  inputProps={{
                    "aria-label": `Style of font file ${index + 1}`,
                  }}
                  sx={{ minWidth: 110 }}
                >
                  {FONT_STYLES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Box>
                  <Tooltip title="Remove font file" arrow>
                    <IconButton
                      aria-label={`Remove font file ${index + 1}`}
                      onClick={() =>
                        set({
                          customFontFiles: files.filter((_, i) => i !== index),
                        })
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
            ))}
            <Box>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                disabled={busy || files.length >= MAX_FONT_FILES}
                sx={{ textTransform: "none", borderRadius: "8px" }}
              >
                {busy ? "Uploading…" : "Upload font file"}
                <input
                  hidden
                  multiple
                  type="file"
                  aria-label="Upload font file"
                  accept={FONT_ACCEPT_ATTRIBUTE}
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </Button>
            </Box>
            <Typography
              variant="caption"
              role={uploadError || errors.customFontFiles ? "alert" : undefined}
              sx={{
                display: "block",
                color:
                  uploadError || errors.customFontFiles
                    ? theme.customColors.status.error.text
                    : theme.customColors.text.secondary,
              }}
            >
              {uploadError ||
                errors.customFontFiles ||
                `${FONT_HINT}. One file per weight (Regular, Medium, Semibold, Bold), or one variable font. Upload only fonts your licence lets you use on your Trust Center.`}
            </Typography>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

// Colours, font and favicon of the whole public page.
const LookSection = ({ form, set, errors }) => {
  // The picker shows each font in itself.
  usePublicFont(fontsUrl(FONTS), "trust-center-font-picker");
  const primary = isHexColor(form.brandColor)
    ? form.brandColor
    : DEFAULT_ACCENT;
  return (
    <Section
      title="Colours, font and icon"
      subtitle="Used across your public page. Changes go live when you publish."
    >
      <BrandColorField
        label="Primary colour"
        value={form.brandColor}
        onChange={(brandColor) => set({ brandColor })}
        error={errors.brandColor}
        presets={BRAND_PRESETS}
        fallback={DEFAULT_ACCENT}
        helperText="Buttons and links. Leave empty for the default."
      />
      <BrandColorField
        label="Secondary colour"
        value={form.secondaryColor}
        onChange={(secondaryColor) => set({ secondaryColor })}
        error={errors.secondaryColor}
        fallback={primary}
        helperText="Highlights: the open tab, checkboxes and counts. Leave empty to use the primary colour."
      />
      <LabeledField
        id="overview-font"
        select
        label="Font"
        value={form.fontFamily}
        onChange={(e) => set({ fontFamily: e.target.value })}
        helperText="For all text on your public page."
        SelectProps={{
          renderValue: (key) =>
            key === CUSTOM_FONT_KEY ? (
              "Custom font"
            ) : (
              <span style={{ fontFamily: fontStack(fontOf(key)) }}>
                {fontOf(key).name}
              </span>
            ),
        }}
      >
        {FONTS.map((font) => (
          <MenuItem
            key={font.key}
            value={font.key}
            sx={{ fontFamily: fontStack(font) }}
          >
            {font.name}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem value={CUSTOM_FONT_KEY}>
          Custom font (Google Fonts or your own files)
        </MenuItem>
      </LabeledField>
      {form.fontFamily === CUSTOM_FONT_KEY && (
        <CustomFontFields form={form} set={set} errors={errors} />
      )}
      <FaviconField
        faviconPath={form.faviconPath}
        onChange={(faviconPath) => set({ faviconPath })}
      />
    </Section>
  );
};

// The two tabs of the edit panel. Branding fields (keys of the form) decide
// which tab a save error belongs to.
export const OVERVIEW_TABS = [
  { value: "details", label: "Details" },
  { value: "branding", label: "Branding" },
];
const BRANDING_FIELDS = [
  "customFontName",
  "customFontFiles",
  "heroTitle",
  "heroImagePath",
  "brandColor",
  "secondaryColor",
  "bannerStartColor",
  "bannerEndColor",
];
// The tab with the first error (or null when there is none).
export const tabWithError = (errors) => {
  const keys = Object.keys(errors);
  if (keys.length === 0) return null;
  return keys.some((k) => !BRANDING_FIELDS.includes(k))
    ? "details"
    : "branding";
};

// Reads branding (logo, banner, colours, font, favicon) from the company
// website. The admin picks what to use in a dialog; it fills the form, so
// nothing changes for buyers until Save changes and Publish.
const SuggestLook = ({ form, set }) => {
  const theme = useTheme();
  const grey = theme.customColors.text.secondary;
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState("");
  return (
    <Box>
      <Button
        variant="outlined"
        onClick={() => setOpen(true)}
        startIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />}
        sx={{
          alignSelf: "flex-start",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8125rem",
          borderRadius: "8px",
          px: 1.5,
          py: 0.75,
          color: grey,
          borderColor: theme.customColors.border.main,
          backgroundColor: theme.customColors.background.light,
          "& .MuiButton-startIcon": { color: grey },
          "&:hover": {
            color: grey,
            borderColor: theme.customColors.border.main,
            backgroundColor: theme.customColors.background.hover,
          },
        }}
      >
        Fetch colours and font from website
      </Button>
      {notice && (
        <Typography
          variant="caption"
          role="status"
          sx={{ display: "block", mt: 0.75, color: grey }}
        >
          {notice}
        </Typography>
      )}
      <FetchBrandingDialog
        open={open}
        website={form.website}
        onClose={() => setOpen(false)}
        onApply={(changes, result, { logoSaved }) => {
          const site = siteName(result.url);
          if (Object.keys(changes).length === 0) {
            setNotice(`Logo saved from ${site}.`);
            return;
          }
          set(changes);
          setNotice(
            `Filled in from ${site}${logoSaved ? " (logo already saved)" : ""}. Check the preview, then Save changes.`
          );
        }}
      />
    </Box>
  );
};

// The Overview editor's settings, in two tabs: Details (what buyers read:
// contact, links, where data is stored) and Branding (how the page looks;
// the editor shows a live preview beside it).
const OverviewForm = ({ form, set, errors, tab = "details" }) => {
  if (tab === "branding")
    return (
      <Stack spacing={3}>
        <SuggestLook form={form} set={set} />
        <CompanyLogoUpload />
        <Divider />
        <BannerSection form={form} set={set} errors={errors} />
        <Divider />
        <LookSection form={form} set={set} errors={errors} />
      </Stack>
    );

  return (
    <Stack spacing={3}>
      {/* Company name, overview and website are the company's own details
          (regodit-customers), edited only in Company Settings. */}
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Your company name, overview and website are edited in{" "}
        <Link
          component={RouterLink}
          to="/company-settings"
          target="_blank"
          rel="noopener"
        >
          Company Settings
        </Link>
        .
      </Typography>
      <Section
        title="Company info"
        subtitle="Shown under your overview. Contact email is required. Leave the others blank to hide them."
      >
        <LabeledField
          id="overview-contact-email"
          label="Contact email"
          placeholder="security@yourcompany.com"
          value={form.securityEmail}
          onChange={(e) => set({ securityEmail: e.target.value })}
          error={errors.securityEmail}
          helperText="Where buyers report security issues."
        />
        <LabeledField
          id="overview-terms-url"
          label="Terms of service link"
          placeholder="https://"
          value={form.termsUrl}
          onChange={(e) => set({ termsUrl: e.target.value })}
          error={errors.termsUrl}
        />
        <LabeledField
          id="overview-privacy-url"
          label="Privacy policy link"
          placeholder="https://"
          value={form.privacyUrl}
          onChange={(e) => set({ privacyUrl: e.target.value })}
          error={errors.privacyUrl}
        />
        <LabeledField
          id="overview-data-residency"
          label="Where customer data is stored"
          placeholder="India (AWS ap-south-1)"
          value={form.dataResidency}
          onChange={(e) => set({ dataResidency: e.target.value })}
        />
      </Section>

      <Divider />

      <Section
        title="Custom links"
        subtitle="Add other pages buyers may want, like your status page, bug bounty or security blog."
      >
        <CustomLinksEditor
          links={form.customLinks}
          errors={errors.customLinks}
          onChange={(customLinks) => set({ customLinks })}
        />
      </Section>
    </Stack>
  );
};

export default OverviewForm;
